(() => {
  if (customElements.get("cart-drawer")) return;

  const SECTION_IDS = ["cart-drawer", "cart-icon-bubble"];

  const routesRoot = () => window.Shopify?.routes?.root || "/";

  const cartChangeUrl = () => {
    const route = window.routes?.cart_change_url;
    if (route) return route.endsWith(".js") ? route : `${route}.js`;
    return `${routesRoot()}cart/change.js`;
  };

  const cartUrl = () => window.routes?.cart_url || `${routesRoot()}cart`;

  class PureCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.activeElement = null;
      this.isUpdating = false;
      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleKeyup = this.handleKeyup.bind(this);
      this.handleExternalUpdate = this.handleExternalUpdate.bind(this);
    }

    connectedCallback() {
      this.addEventListener("click", this.handleClick);
      this.addEventListener("change", this.handleChange);
      this.addEventListener("keyup", this.handleKeyup);
      document.addEventListener("cart:ajax-update", this.handleExternalUpdate);
      this.bindHeaderCartIcon();
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.handleClick);
      this.removeEventListener("change", this.handleChange);
      this.removeEventListener("keyup", this.handleKeyup);
      document.removeEventListener("cart:ajax-update", this.handleExternalUpdate);
    }

    bindHeaderCartIcon() {
      const cartLink = document.querySelector("#cart-icon-bubble");
      if (!cartLink || cartLink.dataset.pureCartBound === "true") return;

      cartLink.dataset.pureCartBound = "true";
      cartLink.setAttribute("aria-haspopup", "dialog");
      cartLink.setAttribute("aria-expanded", "false");

      cartLink.addEventListener("click", (event) => {
        event.preventDefault();
        this.open(cartLink);
      });

      cartLink.addEventListener("keydown", (event) => {
        if (event.code !== "Space") return;
        event.preventDefault();
        this.open(cartLink);
      });
    }

    handleKeyup(event) {
      if (event.code === "Escape") this.close();
    }

    handleClick(event) {
      const closeButton = event.target.closest("[data-pure-cart-close]");
      if (closeButton) {
        event.preventDefault();
        this.close();
        return;
      }

      const removeButton = event.target.closest("[data-pure-remove]");
      if (removeButton) {
        event.preventDefault();
        const line = this.getLine(removeButton);
        if (line) this.changeLine(line, 0, removeButton);
        return;
      }

      const quantityButton = event.target.closest("[data-pure-quantity-change]");
      if (!quantityButton) return;

      event.preventDefault();
      const item = quantityButton.closest("[data-cart-line]");
      const input = item?.querySelector("[data-pure-quantity-input]");
      const line = Number(item?.dataset.cartLine);
      const delta = Number(quantityButton.dataset.pureQuantityChange);

      if (!input || !line || !Number.isFinite(delta)) return;

      const currentQuantity = Number(input.value) || 0;
      const nextQuantity = Math.max(0, currentQuantity + delta);
      this.changeLine(line, nextQuantity, quantityButton);
    }

    handleChange(event) {
      const input = event.target.closest("[data-pure-quantity-input]");
      if (!input) return;

      const line = this.getLine(input);
      const quantity = Math.max(0, Number.parseInt(input.value, 10) || 0);
      if (line) this.changeLine(line, quantity, input);
    }

    handleExternalUpdate(event) {
      const sectionsData = event.detail?.sectionsData || null;
      this.refreshCartDrawer(sectionsData);
    }

    getLine(element) {
      return Number(element.closest("[data-cart-line]")?.dataset.cartLine) || 0;
    }

    getSectionsToRender() {
      return [
        { id: "cart-drawer", selector: "#CartDrawer" },
        { id: "cart-icon-bubble", selector: ".shopify-section" },
      ];
    }

    setActiveElement(element) {
      this.activeElement = element instanceof HTMLElement ? element : null;
    }

    async changeLine(line, quantity, trigger) {
      if (this.isUpdating) return;
      this.setLoading(true);
      this.clearError();

      try {
        const response = await fetch(cartChangeUrl(), {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            line,
            quantity,
            sections: SECTION_IDS,
            sections_url: window.location.pathname,
          }),
        });

        const state = await response.json();
        if (!response.ok || state.errors) {
          throw new Error(
            typeof state.errors === "string"
              ? state.errors
              : state.description || window.cartStrings?.error || "We couldn’t update your cart.",
          );
        }

        this.renderContents(state, { openAfterRender: false });
        this.announce(quantity === 0 ? "Item removed from cart." : "Cart quantity updated.");

        if (window.publish && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: "pure-cart-drawer",
            cartData: state,
          });
        }
      } catch (error) {
        this.showError(error.message || "We couldn’t update your cart. Please try again.");
        const input = trigger?.closest("[data-cart-line]")?.querySelector("[data-pure-quantity-input]");
        if (input) input.value = input.defaultValue;
      } finally {
        this.setLoading(false);
      }
    }

    async refreshCartDrawer(sectionsData = null) {
      if (sectionsData?.sections) {
        this.renderContents(sectionsData);
        return;
      }

      this.setLoading(true);
      try {
        const separator = window.location.search ? "&" : "?";
        const response = await fetch(
          `${window.location.pathname}${window.location.search}${separator}sections=${SECTION_IDS.join(",")}`,
          { headers: { "X-Requested-With": "XMLHttpRequest" } },
        );

        if (!response.ok) throw new Error("Unable to refresh cart.");
        const sections = await response.json();
        this.renderContents({ sections });
      } catch (error) {
        this.showError(error.message || "Unable to refresh cart.");
      } finally {
        this.setLoading(false);
      }
    }

    renderContents(parsedState, options = {}) {
      const sections = parsedState?.sections || parsedState;
      const drawerHtml = sections?.["cart-drawer"];

      if (!drawerHtml) {
        this.showError("The cart updated, but its display could not refresh.");
        return;
      }

      const parsedDocument = new DOMParser().parseFromString(drawerHtml, "text/html");
      const nextDrawer = parsedDocument.querySelector("#CartDrawer");
      const currentDrawer = this.querySelector("#CartDrawer");

      if (!nextDrawer || !currentDrawer) {
        this.showError("The cart updated, but its display could not refresh.");
        return;
      }

      currentDrawer.replaceWith(nextDrawer);
      const itemCount = Number(nextDrawer.dataset.cartCount) || 0;
      this.classList.toggle("is-empty", itemCount === 0);
      this.updateCartIcon(sections?.["cart-icon-bubble"], itemCount);

      this.dispatchEvent(
        new CustomEvent("cart-drawer:render", {
          bubbles: true,
          detail: { parsedState },
        }),
      );
      this.dispatchEvent(
        new CustomEvent("cart-drawer:update", {
          bubbles: true,
          detail: { itemCount },
        }),
      );

      if (options.openAfterRender !== false) this.open();
    }

    updateCartIcon(html, itemCount) {
      if (!html) return;

      const currentIcon = document.querySelector("#cart-icon-bubble");
      const parsedDocument = new DOMParser().parseFromString(html, "text/html");
      const nextSection = parsedDocument.querySelector(".shopify-section");
      if (!currentIcon || !nextSection) return;

      const iconType = currentIcon.getAttribute("data-cart-icon-type");
      currentIcon.innerHTML = nextSection.innerHTML;
      if (iconType) currentIcon.setAttribute("data-cart-icon-type", iconType);
      currentIcon.setAttribute("aria-label", `Cart with ${itemCount} item${itemCount === 1 ? "" : "s"}`);
    }

    open(triggeredBy = null) {
      if (triggeredBy) this.activeElement = triggeredBy;
      this.setAttribute("aria-hidden", "false");

      requestAnimationFrame(() => {
        this.classList.add("active");
        document.body.classList.add("pure-cart-open");
        document.querySelector("#cart-icon-bubble")?.setAttribute("aria-expanded", "true");

        const panel = this.querySelector(".pure-cart__panel");
        const closeButton = this.querySelector(".pure-cart__close");
        if (typeof window.trapFocus === "function" && panel && closeButton) {
          window.trapFocus(panel, closeButton);
        } else {
          closeButton?.focus({ preventScroll: true });
        }
      });

      this.dispatchEvent(
        new CustomEvent("cart-drawer:open", {
          bubbles: true,
          detail: { triggeredBy },
        }),
      );
    }

    close() {
      if (!this.classList.contains("active")) return;
      this.classList.remove("active");
      this.setAttribute("aria-hidden", "true");
      document.body.classList.remove("pure-cart-open");
      document.querySelector("#cart-icon-bubble")?.setAttribute("aria-expanded", "false");

      if (typeof window.removeTrapFocus === "function") {
        window.removeTrapFocus(this.activeElement);
      } else {
        this.activeElement?.focus?.({ preventScroll: true });
      }
    }

    setLoading(isLoading) {
      this.isUpdating = isLoading;
      this.classList.toggle("is-loading", isLoading);
      this.querySelector(".pure-cart__panel")?.setAttribute("aria-busy", String(isLoading));
      this.querySelectorAll("button, input").forEach((control) => {
        if (control.matches("[data-pure-cart-close]")) return;
        control.disabled = isLoading;
      });
    }

    showError(message) {
      const errorElement = this.querySelector("#CartDrawer-CartErrors");
      if (errorElement) errorElement.textContent = message;
    }

    clearError() {
      const errorElement = this.querySelector("#CartDrawer-CartErrors");
      if (errorElement) errorElement.textContent = "";
    }

    announce(message) {
      const liveRegion = this.querySelector("#CartDrawer-LiveRegionText");
      if (!liveRegion) return;
      liveRegion.textContent = "";
      window.setTimeout(() => {
        liveRegion.textContent = message;
      }, 50);
    }
  }

  customElements.define("cart-drawer", PureCartDrawer);

  window.refreshCartDrawer = async (sectionsData = null) => {
    const drawer = document.querySelector("cart-drawer");
    if (drawer?.refreshCartDrawer) await drawer.refreshCartDrawer(sectionsData);
  };
})();
